// ---------------------------------------------------------------------------
// Shared symbol auto-complete hook — canonical 9-prefix vocabulary (F43/F38).
//
// The editor must teach ONLY what the validator accepts: the canonical
// prefixes `local. event. props. item. _results. computed. appData. env.
// route.` (see lib/expression-prefixes.ts — parity-locked against
// @cogo/validator). The previous vocabulary (`blockName.var` bare paths,
// bare `appData`, `_system.*`) was validator-rejected or not even a real
// runtime path, so picking a suggestion produced a broken expression.
//
// Used by:
//   - VariableAutocomplete (expression mode — triggers on `#{`)
//   - SetStateStepForm target field (key mode via useOwningBlockVariableNames)
// ---------------------------------------------------------------------------

import { useMemo } from 'react'
import { useEditorStore } from '@/features/page-editor/store/editor-store'
import { useFlowEditorStore } from '@/features/flow-editor/store/flow-editor-store'
import { useStudioStore } from '@/store/studio-store'
import { resolveStepAtPath } from '@/features/flow-editor/store/flow-slice'
import type { StepPath } from '@/features/flow-editor/store/flow-slice'
import type { BasicStep, LoopStep } from '@cogo/types-v2'
import { ROUTE_PATHS } from '@/features/flow-editor/lib/expression-prefixes'

type AnyBlock = Record<string, unknown>

/** Keys of `block.state.variables` — the block's declared local variables. */
function localVariableKeys(block: AnyBlock | undefined): string[] {
  const state = block?.state as Record<string, unknown> | undefined
  const variables = state?.variables as Record<string, unknown> | undefined
  return variables && typeof variables === 'object' ? Object.keys(variables) : []
}

/** Keys of `block.state.computed` — the block's derived values. */
function computedKeys(block: AnyBlock | undefined): string[] {
  const state = block?.state as Record<string, unknown> | undefined
  const computed = state?.computed as Record<string, unknown> | undefined
  return computed && typeof computed === 'object' ? Object.keys(computed) : []
}

/** Same flow-owner resolution as StepInspector (full then bare flow name). */
function findFlowOwnerName(blocks: Record<string, AnyBlock>, flowName: string): string | null {
  const lookup = (name: string): string | null => {
    for (const [blockName, block] of Object.entries(blocks)) {
      const flow = block.flow as Record<string, unknown> | undefined
      if (!flow) continue
      if (flow[name] && typeof flow[name] === 'object') return blockName
      const flows = flow.flows as Record<string, unknown> | undefined
      if (flows && flows[name]) return blockName
    }
    return null
  }
  const direct = lookup(flowName)
  if (direct) return direct
  const dotIdx = flowName.lastIndexOf('.')
  return dotIdx > 0 ? lookup(flowName.slice(dotIdx + 1)) : null
}

/**
 * Bare variable names declared by the block that owns the selected flow —
 * the setState `target` suggestion pool (F44: target is a BARE name, never
 * `#{}`-wrapped). Falls back to the union across editor blocks when the
 * owning block cannot be resolved.
 */
export function useOwningBlockVariableNames(): string[] {
  const flows = useFlowEditorStore((s) => s.flows)
  const selectedFlowId = useFlowEditorStore((s) => s.selectedFlowId)
  const project = useStudioStore((s) => s.currentProject)
  const editorBlocks = (useEditorStore((s) => s.blocks) ?? {}) as unknown as Record<string, AnyBlock>

  return useMemo(() => {
    const projectBlocks = (project?.blocks ?? {}) as unknown as Record<string, AnyBlock>
    const flowName = flows.find((f) => f.id === selectedFlowId)?.name ?? null
    const ownerName = flowName ? findFlowOwnerName(projectBlocks, flowName) : null

    const names = new Set<string>()
    if (ownerName) {
      for (const key of localVariableKeys(projectBlocks[ownerName])) names.add(key)
      // The same block may also be hydrated in the editor store
      for (const key of localVariableKeys(editorBlocks[ownerName])) names.add(key)
    } else {
      for (const block of Object.values(editorBlocks)) {
        for (const key of localVariableKeys(block)) names.add(key)
      }
    }
    return [...names]
  }, [flows, selectedFlowId, project, editorBlocks])
}

/**
 * Collects the canonical expression symbols available in the current editing
 * context. Returns a flat list like `["local.email", "appData.userId",
 * "_results.data", "event.", "props.", "route.params", ...]` — every entry
 * starts with one of the 9 canonical prefixes.
 */
export function useAllSymbols(): string[] {
  const blocks = (useEditorStore((s) => s.blocks) ?? {}) as unknown as Record<string, AnyBlock>
  const flowSteps = useFlowEditorStore((s) => s.flowSteps)
  const flows = useFlowEditorStore((s) => s.flows)
  const selectedFlowId = useFlowEditorStore((s) => s.selectedFlowId)
  const selectedStepPath = useFlowEditorStore((s) => s.selectedStepPath)
  const project = useStudioStore((s) => s.currentProject)

  return useMemo(() => {
    const result: string[] = []
    const push = (sym: string) => {
      if (!result.includes(sym)) result.push(sym)
    }

    const projectBlocks = (project?.blocks ?? {}) as unknown as Record<string, AnyBlock>
    const selectedFlow = flows.find((f) => f.id === selectedFlowId)
    const flowName = selectedFlow?.name ?? null
    const ownerName = flowName ? findFlowOwnerName(projectBlocks, flowName) : null

    // local.* — the owning block's declared variables; union across editor
    // blocks when the owner is unknown (still under the canonical prefix).
    const localBlocks: AnyBlock[] = ownerName
      ? [projectBlocks[ownerName], blocks[ownerName]].filter(Boolean) as AnyBlock[]
      : Object.values(blocks)
    let hasLocalKeys = false
    for (const block of localBlocks) {
      for (const key of localVariableKeys(block)) {
        hasLocalKeys = true
        push(`local.${key}`)
      }
    }
    if (!hasLocalKeys) push('local.')

    // appData.* — project-level shared state keys from any block
    let hasAppDataKeys = false
    for (const block of [...Object.values(blocks), ...Object.values(projectBlocks)]) {
      const state = block.state as Record<string, unknown> | undefined
      const appData = state?.appData as Record<string, unknown> | undefined
      if (appData && typeof appData === 'object') {
        for (const key of Object.keys(appData)) {
          hasAppDataKeys = true
          push(`appData.${key}`)
        }
      }
    }
    if (!hasAppDataKeys) push('appData.')

    // computed.* — derived values declared by the same blocks
    let hasComputedKeys = false
    for (const block of localBlocks) {
      for (const key of computedKeys(block)) {
        hasComputedKeys = true
        push(`computed.${key}`)
      }
    }
    if (!hasComputedKeys) push('computed.')

    // _results.* — fallback entries + upstream-step harvest (resultTarget /
    // responseKey params and named steps, which auto-capture at _results.<name>)
    const resultTargets = new Set<string>(['_results.data', '_results.error', '_results.status'])
    for (const step of flowSteps) {
      if ('params' in step) {
        const basic = step as BasicStep
        const rt = (basic.params?.resultTarget ?? basic.params?.responseKey) as string | undefined
        if (typeof rt === 'string' && rt.startsWith('#_results.')) {
          resultTargets.add(rt.slice(1)) // strip leading #
        } else if (typeof rt === 'string' && rt.length > 0) {
          resultTargets.add(rt.startsWith('_results.') ? rt : `_results.${rt}`)
        }
        if (typeof basic.name === 'string' && basic.name.length > 0) {
          resultTargets.add(`_results.${basic.name}`)
        }
      }
    }
    for (const rt of resultTargets) push(rt)

    // event.* — only for event-triggered flows (init/lifecycle/manual flows
    // never receive an event payload). Missing trigger defaults to event:
    // the UI event binding is the real wiring, the trigger value decorative.
    const trigger = selectedFlow?.trigger
    if (trigger !== 'init' && trigger !== 'lifecycle' && trigger !== 'manual') {
      push('event.')
    }

    // item.* — only inside a loop body, under the loop's declared itemVar.
    // The selection may be nested arbitrarily deep; EVERY ancestor loop whose
    // body the path descends through contributes its itemVar (inner scopes
    // see outer iteration variables).
    if (selectedStepPath && selectedStepPath.length >= 3) {
      for (let i = 1; i + 1 < selectedStepPath.length; i += 2) {
        if (selectedStepPath[i] !== 'steps') continue
        const ancestor = resolveStepAtPath(flowSteps, selectedStepPath.slice(0, i) as StepPath) as LoopStep | null
        if (ancestor?.type === 'loop') {
          push(`${ancestor.itemVar || 'item'}.`)
        }
      }
    }

    // props. / env. — canonical prefixes without a derivable key source
    push('props.')
    push('env.')

    // route.* — fixed read-only sub-structure
    for (const path of ROUTE_PATHS) push(path)

    return result
  }, [blocks, flowSteps, flows, selectedFlowId, selectedStepPath, project])
}

/**
 * Returns filtered symbol suggestions when cursor is after `#{` in the text.
 * Returns `null` when there is no `#{` trigger (no dropdown should show).
 * `override` replaces the context-derived symbol pool when provided.
 */
export function useSymbolSuggestions(
  text: string,
  cursorPos: number,
  override?: string[],
): string[] | null {
  const symbols = useAllSymbols()

  return useMemo(() => {
    const pool = override ?? symbols
    const textBefore = text.slice(0, cursorPos)
    const hashMatch = textBefore.match(/#{([^}]*)$/)
    if (!hashMatch) return null
    const partial = hashMatch[1].toLowerCase()
    // Exact matches are filtered: once the typed text equals the entry there
    // is nothing left to suggest (and a just-picked prefix stub would
    // otherwise re-offer itself forever).
    return pool.filter((s) => {
      const lower = s.toLowerCase()
      return lower.startsWith(partial) && lower !== partial
    })
  }, [text, cursorPos, symbols, override])
}
